/**
 * Lightweight per-page meta-tag manager. Avoids the dependency footprint of `react-helmet-async`
 * — the website only needs to set/clear a handful of tags per route.
 *
 * `data-qh-dynamic` attribute marks every tag we own so cleanup never touches static tags
 * (charset, viewport, fonts) the index.html ships with.
 */

const DYNAMIC_ATTR = 'data-qh-dynamic';

interface MetaInput {
  title?: string;
  description?: string;
  canonical?: string;
  openGraph?: {
    title?: string;
    description?: string;
    url?: string;
    type?: string;
    image?: string;
  };
}

export function setDocumentMeta(input: MetaInput): void {
  if (typeof document === 'undefined') return;

  if (input.title) document.title = input.title;

  if (input.description) {
    upsertMeta({ name: 'description' }, input.description);
  }
  if (input.canonical) {
    upsertLink({ rel: 'canonical' }, { href: input.canonical });
  }
  if (input.openGraph) {
    const og = input.openGraph;
    if (og.title) upsertMeta({ property: 'og:title' }, og.title);
    if (og.description) upsertMeta({ property: 'og:description' }, og.description);
    if (og.url) upsertMeta({ property: 'og:url' }, og.url);
    if (og.type) upsertMeta({ property: 'og:type' }, og.type);
    if (og.image) upsertMeta({ property: 'og:image' }, og.image);
    upsertMeta({ property: 'og:site_name' }, 'QuantumHire');
  }
}

export function removeDynamicMeta(): void {
  if (typeof document === 'undefined') return;
  document
    .querySelectorAll(`[${DYNAMIC_ATTR}="meta"], [${DYNAMIC_ATTR}="link"]`)
    .forEach((el) => el.parentElement?.removeChild(el));
}

/**
 * Upsert a JSON-LD <script type="application/ld+json"> block keyed by `id`. Replacing the same
 * id on every render avoids stacking multiple JobPosting blocks if React re-runs the effect.
 */
export function setJsonLd(id: string, payload: unknown): void {
  if (typeof document === 'undefined') return;
  const tagId = `qh-jsonld-${id}`;
  const existing = document.getElementById(tagId);
  if (existing) existing.parentElement?.removeChild(existing);
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.id = tagId;
  script.setAttribute(DYNAMIC_ATTR, 'jsonld');
  script.textContent = JSON.stringify(payload);
  document.head.appendChild(script);
}

export function removeJsonLd(id: string): void {
  if (typeof document === 'undefined') return;
  const tagId = `qh-jsonld-${id}`;
  const existing = document.getElementById(tagId);
  if (existing) existing.parentElement?.removeChild(existing);
}

function upsertMeta(matchAttrs: Record<string, string>, content: string): void {
  const selector = Object.entries(matchAttrs).map(([k, v]) => `[${k}="${cssEscape(v)}"]`).join('');
  let el = document.head.querySelector(`meta${selector}`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    for (const [k, v] of Object.entries(matchAttrs)) el.setAttribute(k, v);
    el.setAttribute(DYNAMIC_ATTR, 'meta');
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(matchAttrs: Record<string, string>, attrs: Record<string, string>): void {
  const selector = Object.entries(matchAttrs).map(([k, v]) => `[${k}="${cssEscape(v)}"]`).join('');
  let el = document.head.querySelector(`link${selector}`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    for (const [k, v] of Object.entries(matchAttrs)) el.setAttribute(k, v);
    el.setAttribute(DYNAMIC_ATTR, 'link');
    document.head.appendChild(el);
  }
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
}

function cssEscape(s: string): string {
  return s.replace(/"/g, '\\"');
}
